import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { FULL_SCREEN_STATE_MIN_H } from '@/lib/constants/layout';

export interface LinkStatusPill {
  label: string;
  /** Highlighted (the status that actually applies) vs. dimmed. */
  active: boolean;
}

export interface LinkStatusCardProps {
  icon: ReactNode;
  heading: string;
  /** Usually a plain string; a ReactNode when a caller needs to highlight part of it (e.g. StartupAlreadyExistsState bolding the existing company's name). */
  body: ReactNode;
  /** Optional status pills (e.g. "Link Expired" / "Link Revoked") - omit for states with no pill pair. */
  pills?: LinkStatusPill[];
  ctaLabel: string;
  /** Navigates to this path. Omit when using `onCtaClick` instead. */
  ctaTo?: string;
  /**
   * `outline` (default) renders `ctaLabel` as the usual bordered button -
   * right for the plain single-CTA states. `link` renders it as a plain
   * text link instead - use alongside `primaryAction` so the screen reads
   * as one clear action (the button) plus a lower-key way out (the link),
   * rather than two competing buttons.
   */
  ctaVariant?: 'outline' | 'link';
  /**
   * Runs a click handler instead of navigating (e.g. "log out" on the
   * invite wrong-account state). Exactly one of `ctaTo`/`onCtaClick` should
   * be set - `onCtaClick` takes precedence if both are.
   */
  onCtaClick?: () => void;
  footnote?: string;
  /**
   * Set when this card IS the screen - nothing else on the page - so it
   * centers in the whole region it owns (`FULL_SCREEN_STATE_MIN_H`: the
   * viewport minus the surrounding layout's declared `--app-chrome-offset`)
   * rather than in a guessed `70vh` box. Works unchanged wherever the state is
   * mounted, including a state mounted in more than one place, because the
   * offset is inherited from the layout rather than passed in here.
   *
   * Leave unset when the card renders as one block among others on a page
   * (e.g. `StartupAlreadyExistsState`, which sits under `CreateStartupPage`'s
   * heading), or under a layout that puts more chrome BELOW it - the invite
   * screens sit above `PublicLayout`'s footer, so a full-viewport box there
   * would push the footer off-screen on every load.
   *
   * Do NOT reintroduce a percentage height (`h-full`) for this: `AppLayout`
   * gives `<main className="flex-1">` no *definite* height, so a percentage
   * inside it collapses to content height and the card sits high with a large
   * gap beneath it (MYN-131). See `FULL_SCREEN_STATE_MIN_H`.
   */
  fullScreen?: boolean;
  /** Overrides the card's default width (`max-w-120`, 480px) for states whose copy needs more room. */
  maxWidthClassName?: string;
  /**
   * The card alone, at its column's full width, with no centring box around
   * it: for a page that already lays out its own column, such as the Issue
   * Report journey, where it sits under the progress bar like the other steps.
   */
  inline?: boolean;
  /**
   * A prominent (filled) action rendered above the outline `ctaLabel` button
   * - e.g. "Request Access" above "Go to Dashboard". Omit for the plain
   * single-CTA states (invite-expired, work-email-gate, etc.).
   */
  primaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    pendingLabel?: string;
  };
}

/**
 * Generic full-page "this link no longer works" card - the shape shared by
 * the invite-expired screen (MYN-43/47) and the Shared Startup Profile
 * link-expired screen (MYN-58). Promoted out of `InviteExpiredState` per
 * mnr-standards' promotion rule (used by one feature -> keep it there; a
 * second feature needing it -> promote to `common/`).
 */
export function LinkStatusCard({
  icon,
  heading,
  body,
  pills,
  ctaLabel,
  ctaTo,
  ctaVariant = 'outline',
  onCtaClick,
  footnote,
  fullScreen = false,
  maxWidthClassName = 'max-w-120',
  inline = false,
  primaryAction,
}: LinkStatusCardProps) {
  const heightClassName = fullScreen ? FULL_SCREEN_STATE_MIN_H : 'min-h-[70vh]';
  // Inline it sits under the page's own heading, so its title is an h2 sized
  // like the journey's other step titles; centred it is the page's only h1.
  const layouts = {
    inline: { frame: 'flex', width: 'max-w-none', Heading: 'h2', heading: 'font-sans text-[1.625rem] font-bold text-navy' },
    centred: {
      frame: `flex items-center justify-center px-4 py-16 ${heightClassName}`,
      width: maxWidthClassName,
      Heading: 'h1',
      heading: 'font-sans text-h1 text-navy',
    },
  } as const;
  const layout = layouts[inline ? 'inline' : 'centred'];
  const Heading = layout.Heading;

  return (
    <div className={layout.frame}>
      <div className={`flex w-full ${layout.width} flex-col items-center gap-8 rounded-card bg-white p-12 shadow-card`}>
        <span
          aria-hidden="true"
          className="flex h-18 w-18 items-center justify-center rounded-full bg-red text-white"
        >
          {icon}
        </span>

        <div className="flex flex-col items-center gap-3 text-center">
          <Heading className={layout.heading}>{heading}</Heading>
          <p className="text-sm text-subtext">{body}</p>
        </div>

        {pills && pills.length > 0 && (
          <>
            <div className="h-px w-full bg-page-bg" />
            <div className="flex items-start justify-center gap-3">
              {pills.map((pill) => (
                <span
                  key={pill.label}
                  className={`rounded-full border-[1.5px] border-red px-4 py-1.5 font-sans text-[11px] font-semibold text-red ${
                    pill.active ? 'bg-red/10' : 'bg-red/5 opacity-50'
                  }`}
                >
                  {pill.label}
                </span>
              ))}
            </div>
          </>
        )}

        <div className="flex flex-col items-center gap-3">
          {primaryAction && (
            <Button
              type="button"
              variant="green"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
            >
              {primaryAction.disabled && primaryAction.pendingLabel
                ? primaryAction.pendingLabel
                : primaryAction.label}
            </Button>
          )}
          {onCtaClick ? (
            <Button type="button" variant={ctaVariant} onClick={onCtaClick}>
              {ctaLabel}
            </Button>
          ) : (
            <Button asChild variant={ctaVariant}>
              <Link to={ctaTo ?? '/'}>{ctaLabel}</Link>
            </Button>
          )}
        </div>
        {footnote && (
          <p className="max-w-xs text-center text-[11px] text-slate">{footnote}</p>
        )}
      </div>
    </div>
  );
}
